import {
  CommerceItemResponse, CommerceType, CommerceItemHashTag,
  HashTagObj,
  CommercePermission,
  PayingOption,
  SurveyOption,
  CommerceItemCategory,
  NotificationPublishType,
  ReviewStatusType,
  PaymentStatusType,
  CommerceApplicationResponse,
  CommerceAttendeeResponse,
  CommerceAttendanceRecordResponse,
  CommerceReviewHistoryResponse,
  ApplicationStatusType,
  ApplyStatusType,
  RedemptionRecordResponse,
  PaymentMethodType,
  ExportReceiptPDFResponse,
} from 'app/models/CommerceItem';
import { Filter, ListResult, PublishStatusType } from 'app/models/Common';
import { DriveType, FolderType } from 'app/models/Drive';
import { FromSVType } from 'app/models/Shop';
import { CommerceApplicationSurveyResponse } from 'app/models/Survey';

import Axios from '.';

/**
 * List All Commerce Item By Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/admin)
 */

export interface GetCommerceItemListParams extends Filter<CommerceItemResponse> {
  commerceType?: CommerceType;
  publishStatus?: PublishStatusType;
  reviewStatus?: ReviewStatusType;
  isCountingDown?: boolean;
  title?: string;
  startDate?: string;
  endDate?: string;
  shopId?: number;
  fromSV?: FromSVType;
  commerceItemCategoryId?: number;
  hashTag?: string;
  hasPendingApplyStatus?: boolean;
}

export const getCommerceItemList = async (params: GetCommerceItemListParams) => {
  const { data } = await Axios.get<ListResult<CommerceItemResponse>>('/commerces/items/admin', { params });
  return data;
};

/**
 * Get One Commerce By CommerceId, Optional Login (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/-commerceItemId-)
 */

export const getOneCommerceItem = async (commerceItemId: number | string) => {
  const { data } = await Axios.get<CommerceItemResponse>(`/commerces/items/${commerceItemId}`);
  return data;
};

interface CommonCommercePurchaseOptionsProps {
  commercePurchaseOptionId?: number
  image: string | null
  commercePurchaseOptionTitle?: string
  commercePurchaseOptionSubtitle?: string
  payingOption: PayingOption;
  commercePurchaseOptionQuota?: number
  commercePurchaseOptionMin?: number | null
  commercePurchaseOptionMax?: number | null
  commercePurchaseOptionPerUserMin?: number | null
  commercePurchaseOptionPerUserMax?: number | null
  pointEarnAfterApply?: number | null
  pointEarnAfterCheckIn?: number | null
  commercePurchaseOptionMediaFolderType?: FolderType | null
  commercePurchaseOptionMediaDriveType?: DriveType | null
  commercePurchaseOptionMediaPath?: string
  surveyOption: SurveyOption
  surveyId?: number | null;
  isAllowWaitList?: boolean;
  waitListQuota?: number | null;
  isAllowGuest?: boolean;
  ordering: number;
}

interface CreateCommerceScheduleProps {
  commerceScheduleId?: number;
  commerceScheduleTitle?: string;
  startTime: string;
  endTime: string;
  location?: string | null;
  isOnline?: boolean;
  zoomRoomNumber?: string | null;
  zoomRoomPassword?: string | null;
  pointEarnAfterCheckIn?: number | null;
  afterCheckInSurveyId?: number | null;
  materialFolderType?: FolderType | null;
  materialDriveType?: DriveType | null;
  materialPath?: string | null;
  materialHiddenPath?: string | null;
  ordering: number;
}

interface CreateEventDetailProps {
  eventDetailId?: number;
  CommerceSchedule: CreateCommerceScheduleProps;
}

interface CreateCommerceItemCommonProps {
  shopId: number;
  commerceItemCategoryId: number;
  CommerceItemHashTags: HashTagObj[];
  title: string;
  coverPhoto: string;
  images: string[];
  remarks?: string;
  referenceCode?: string | null;
  contactPerson?: string | null;
  contactPhoneNumber?: string | null;
  subtitle?: string;
  description: string;
  mediaFolderType?: FolderType | null;
  mediaDriveType?: DriveType | null;
  mediaPath?: string;
  attachmentFolderType?: FolderType | null;
  attachmentDriveType?: DriveType | null;
  attachmentPath?: string | null;
  attachmentHiddenRoot?: string | null;
  publishedDate?: string | null;
  publishEndDate?: string | null;
  CommercePermissions: CommercePermission[];
  afterPaymentAttachmentDriveType?: DriveType | null;
  afterPaymentAttachmentFolderType?: FolderType | null;
  afterPaymentAttachmentPath?: string | null;
  afterPaymentAttachmentHiddenRoot?: string | null;
  reviewStatus?: ReviewStatusType;
  ScheduledNotification?: CreateCommerceItemPushNotificationProps;
}

interface CreateCommerceItemPushNotificationProps {
  publishType: NotificationPublishType;
  scheduledTime?: string | null;
  title?: string;
  content?: string | null;
  image?: string | null;
  tags?: string[];
  scheduledNotificationId?: number;
}

interface CreateEventPurchaseOptionProps extends CommonCommercePurchaseOptionsProps {
  isAllowGuest: boolean
}

interface CreateEventCommerceItemProps extends CreateCommerceItemCommonProps {
  commerceType: '活動';
  caution?: string;
  paidReminder?: string;
  EventDetail: CreateEventDetailProps;
  isCharity?: boolean;
  acceptDonation?: boolean;
  surveyId?: number | null;
  perOrderMin?: number | null;
  perOrderMax?: number | null;
  perUserTotalMin?: number | null;
  perUserTotalMax?: number | null;
  openForSaleDate?: string | null;
  discontinueDate?: string | null;
  CommercePurchaseOptions: CreateEventPurchaseOptionProps[];
}

interface CreateCourseDetailProps {
  courseDetailId?: number;
  courseCode: string;
  contactPerson: string;
  contactPhoneNumber: string;
  pointEarnAfterApply?: number | null;
  afterPaymentAttachmentFolderType?: FolderType | null;
  afterPaymentAttachmentDriveType?: DriveType | null;
  afterPaymentAttachmentPath?: string | null;
  afterPaymentAttachmentHiddenRoot?: string | null;
  CommerceSchedules: CreateCommerceScheduleProps[];
  isApplyPerLesson: boolean;
}

interface CreateCoursePurchaseOptionProps extends CommonCommercePurchaseOptionsProps {
  isAllowMember?: boolean;
  isAllowPreAgent?: boolean;
  preAgentSurveyOption?: SurveyOption;
  preAgentSurveyId?: number | null;
}

interface CreateCourseCommerceItemProps extends CreateCommerceItemCommonProps {
  commerceType: '課程';
  caution?: string;
  CourseDetail: CreateCourseDetailProps;
  CommercePurchaseOptions: CreateCoursePurchaseOptionProps[];
  acceptDonation?: boolean;
  surveyId?: number | null;
  perOrderMin?: number | null;
  perOrderMax?: number | null;
  perUserTotalMin?: number | null;
  perUserTotalMax?: number | null;
  openForSaleDate?: string | null;
  discontinueDate?: string | null;
}

interface CreateProductCommerceItemProps extends CreateCommerceItemCommonProps {
  commerceType: '貨品';
  caution?: string;
  paidReminder?: string;
  isCharity?: boolean;
  acceptDonation?: boolean;
  allowShare?: boolean;
  displayQRcode?: boolean;
  surveyId?: number | null;
  perOrderMin?: number | null;
  perOrderMax?: number | null;
  perUserTotalMin?: number | null;
  perUserTotalMax?: number | null;
  openForSaleDate?: string | null;
  discontinueDate?: string | null;
  redemptionDueDate?: string | null;
  CommercePurchaseOptions: CommonCommercePurchaseOptionsProps[];
}

interface CreateNewsCommerceItemProps extends CreateCommerceItemCommonProps {
  commerceType: '資訊';
  surveyId?: number | null;
}

export type CreateCommerceItemProps =
  CreateEventCommerceItemProps
  | CreateCourseCommerceItemProps
  | CreateProductCommerceItemProps
  | CreateNewsCommerceItemProps;

/**
 * Create A Commerce Item, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items)
 */

export const createOneCommerceItem = async (params: { CommerceItem: CreateCommerceItemProps }) => {
  const { data } = await Axios.post<CommerceItemResponse>('/commerces/items', params);
  return data;
};

/**
 * Update One Commerce Item By CommerceItemId, Admin Only (-)
 */

export const updateOneCommerceItem = async (
  commerceItemId: number | string,
  params: { CommerceItem: CreateCommerceItemProps },
) => {
  const { data } = await Axios.patch<CommerceItemResponse>(`/commerces/items/${commerceItemId}`, params);
  return data;
};

/**
 * Delete One Commerce By CommerceId, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#delete-/v2/commerces/items/-commerceItemId-)
 */

export const deleteOneCommerceItem = async (commerceItemId: number | string) => {
  await Axios.delete(`/commerces/items/${commerceItemId}`);
};

export interface GetUniqueCommerceItemHashTagParams {
  hashTagPrefix?: string
}

export interface GetCommerceItemCategoryListParams extends Filter<CommerceItemCategory> {}

/**
 * List All Commerce Item Category (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/itemCategory)
 */

export const getCommerceItemCategoryList = async (params: GetCommerceItemCategoryListParams) => {
  const { data } = await Axios.get<ListResult<CommerceItemCategory>>('/commerces/itemCategory', { params });
  return data;
};

/**
 * Get One Commerce Item Category By commerceItemCategoryId (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/itemCategory/-commerceItemCategoryId-)
 */

export const getOneCommerceItemCategory = async (commerceItemCategoryId: number) => {
  const { data } = await Axios.get<CommerceItemCategory>(`/commerces/itemCategory/${commerceItemCategoryId}`);
  return data;
};

/**
 * List All Unique Commerce Item HashTag (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/hashTag)
 */

export const getUniqueCommerceItemHashTag = async (params: GetUniqueCommerceItemHashTagParams) => {
  const { data } = await Axios.get<CommerceItemHashTag[]>('/commerces/items/hashTag', { params });
  return data;
};

/**
 * Get Permitted User Count, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items/permittedUserCount)
 */

export const getPermittedUserCount = async (params: { CommercePermissions: Omit<CommercePermission, 'allowPreview'>[] }) => {
  const { data } = await Axios.post<{ count: number }>('/commerces/items/permittedUserCount', params);
  return data;
};

export interface GetCommerceApplicationListParams extends Filter<CommerceItemResponse> {
  search?: string;
  commerceItemId?: number;
  commerceItemCategoryId?: number;
  commerceItemType?: CommerceType;
  paymentStatuses?: PaymentStatusType;
  applicationStatuses?: ApplicationStatusType | ApplicationStatusType[];
  userId?: number
  commercePurchaseOptionId?: number;
  shopId?: number;
}

/**
 * List All Commerce Application, Login Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/application)
 */

export const getCommerceApplicationList = async (params: GetCommerceApplicationListParams) => {
  const { data } = await Axios.get<ListResult<CommerceApplicationResponse>>('/commerces/items/application', { params });
  return data;
};

/**
 * List All Commerce Application, Login Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/application)
 */

export const getOneCommerceApplication = async (commerceApplicationId: number) => {
  const { data } = await Axios.get<CommerceApplicationResponse>(
    `/commerces/items/application/${commerceApplicationId}`,
  );
  return data;
};

export interface GetCommerceItemAttendeeListParams extends Filter<CommerceAttendeeResponse> {
  commerceItemId: number,
  search?: string;
  applicationStatuses?: ApplicationStatusType[];
  paymentStatuses?: PaymentStatusType;
}

/**
 * List All Commerce Attendees By commerceItemId, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/-commerceItemId-/attendees)
 */

export const getCommerceItemAttendeeList = async (
  { commerceItemId, ...params }: GetCommerceItemAttendeeListParams,
) => {
  const { data } = await Axios.get<ListResult<CommerceAttendeeResponse>>(
    `/commerces/items/${commerceItemId}/attendees`,
    { params },
  );
  return data;
};

export interface GetCommerceItemUnAttendedListParams extends Filter<CommerceAttendanceRecordResponse> {
  commerceScheduleId: number,
  search?: string;
  applicationStatuses?: ApplicationStatusType;
  paymentStatuses?: PaymentStatusType;
}

/**
 * List All Unattended Commerce Attendees By commerceItemId, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/-commerceItemId-/attendees)
 */

export const getCommerceItemUnAttendeeList = async (
  { commerceScheduleId, ...params }: GetCommerceItemUnAttendedListParams,
) => {
  const { data } = await Axios.get<ListResult<CommerceAttendeeResponse>>(
    `/commerces/items/commerceSchedule/${commerceScheduleId}/unattended`,
    { params },
  );
  return data;
};

export interface GetCommerceAttendanceRecordListParams extends Filter<CommerceAttendanceRecordResponse> {
  commerceScheduleId: number,
  search?: string;
  userId?: number;
  preAgentId?: number;
  hasCheckInRecord?: boolean;
}

/**
 * List All Commerce Attendees By commerceScheduleId, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/commerceSchedule/-commerceScheduleId-/attendanceRecord)
 */

export const getCommerceAttendanceRecordList = async (
  {
    commerceScheduleId,
    ...params
  }: GetCommerceAttendanceRecordListParams,
) => {
  const { data } = await Axios.get<ListResult<CommerceAttendeeResponse>>(
    `/commerces/items/commerceSchedule/${commerceScheduleId}/attendanceRecord`,
    { params },
  );
  return data;
};

export interface GetCommerceReviewHistoryListParams extends Filter<CommerceReviewHistoryResponse> {
  commerceItemId?: number;
  commerceBannerId?: number;
  adminId?: number;
  dateFrom?: string;
  dateTo?: string;
  applyStatus?: ApplyStatusType;
}

/**
 * List all the reviewHistory (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/reviewHistory)
 */

export const getCommerceReviewHistoryList = async (
  params : GetCommerceReviewHistoryListParams,
) => {
  const { data } = await Axios.get<ListResult<CommerceReviewHistoryResponse>>(
    '/commerces/reviewHistory',
    { params },
  );
  return data;
};

/**
 * Count All Commerce Item (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items)
 */

export const countCommerceItem = async (params: GetCommerceItemListParams) => {
  const { data } = await Axios.get<{ count: number }>('/commerces/items/count', { params });
  return data;
};

export interface CreateOrUpdateAttendanceRecordParams {
  attendanceRecordId?: number
  commerceAttendeeId: number
  commerceScheduleId: number
  checkInDateTime?: string | null
  checkOutDateTime?: string | null
}

/**
 * Create Or Update One Attendance Record, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items/application/attendanceRecord)
 */

export const createOrUpdateOneAttendanceRecord = async (params: CreateOrUpdateAttendanceRecordParams) => {
  const { data } = await Axios.post<CommerceAttendanceRecordResponse>('/commerces/items/application/attendanceRecord', params);
  return data;
};

export interface BatchCreateOrUpdateAttendanceRecordParams {
  commerceAttendeeIds: number[]
  commerceScheduleId: number
  checkInDateTime?: string | null
  checkOutDateTime?: string | null
}

/**
 * Create Or Update Attendance Record In Batch, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items/application/attendanceRecordBatch)
 */

export const batchCreateOrUpdateOneAttendanceRecord = async (params: BatchCreateOrUpdateAttendanceRecordParams) => {
  const { data } = await Axios.post('/commerces/items/application/attendanceRecordBatch', params);
  return data;
};

/**
 * Cancel One Event Application By CommerceApplicationId, Login Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#put-/v2/commerces/items/application/-commerceApplicationId-/cancel)
 */

export const cancelOneCommerceApplication = async (commerceApplicationId: number) => {
  await Axios.put(`/commerces/items/application/${commerceApplicationId}/cancel`);
};

/**
 * Accept waiting list Application. Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items/application/-commerceApplicationId-/acceptWaitingList)
 */

export const acceptWaitingListApplication = async (commerceApplicationId: number) => {
  await Axios.post(`/commerces/items/application/${commerceApplicationId}/acceptWaitingList`);
};

/**
 * Apply One ReviewHistory, App User Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/reviewHistory/-reviewHistoryId-/apply)
 */

export const applyOneReviewHistory = async (reviewHistoryId: number) => {
  await Axios.post(`/commerces/reviewHistory/${reviewHistoryId}/apply`);
};

/**
 * Delete One ReviewHistory, App User Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/reviewHistory/-reviewHistoryId-/reject)
 */

export const rejectOneReviewHistory = async (reviewHistoryId: number) => {
  await Axios.post(`/commerces/reviewHistory/${reviewHistoryId}/reject`);
};

export interface GetRedemptionRecordListParams extends Filter<RedemptionRecordResponse> {
  commerceItemId?: number;
  commerceApplicationId?: number;
  commercePurchaseOptionId?: number;
  shopId?: number;
}

/**
 * List Redemption Record, Login Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/redemptionRecords)
 */

export const getRedemptionRecordList = async (params: GetRedemptionRecordListParams) => {
  const { data } = await Axios.get<ListResult<RedemptionRecordResponse>>('/commerces/items/redemptionRecords', { params });
  return data;
};

interface RedeemApplicationOptionItem {
  commercePurchaseOptionId: number;
  numberOfOption: number;
}

export interface RedeemOneCommerceApplicationParams {
  commerceApplicationOptions: RedeemApplicationOptionItem[]
}

/**
 * Redeem One Commerce Application By code, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items/application/product/-code-/redemption)
 */

export const redeemOneCommerceApplication = async (code: string, params: RedeemOneCommerceApplicationParams) => {
  await Axios.post(`/commerces/items/application/product/${code}/redemption`, params);
};

export interface GetCommerceApplicationSurveyParams {
  CommerceApplicationOptions: {
    commerceApplicationOptionId?: number;
    commercePurchaseOptionId: number;
    numberOfOption: number
  }[]
  CommerceAttendees: {
    commerceAttendeeId?: number | null;
    userId?: number | null;
    preAgentId?: number | null;
  }[]
  userId: number;
}

/**
 * Get A Commerce Application Survey Response Format, AppUser Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items/-commerceItemId-/surveyResponseFormat)
 */

export const getCommerceApplicationSurvey = async (
  commerceItemId: number,
  params: GetCommerceApplicationSurveyParams,
) => {
  const { data } = await Axios.post<{ SurveyResponses: CommerceApplicationSurveyResponse[] }>(
    `/commerces/items/${commerceItemId}/surveyResponseFormat`,
    params,
  );
  return data;
};

interface CommerceApplicationSurveyResponseProps {
  commercePurchaseOptionId?: number | null;
  commerceApplicationId?: number | null;
  commerceItemId: number;
  commerceScheduleId?: number | null;
  commerceAttendeeId?: number | null;
  commerceScheduleApplicationId?: number | null;
  date?: string;
  managerName?: string;
  tutorName?: string;
  responses: {
    questionId: number;
    text?: string;
    optionAnswers?: {
      questionOptionId: number;
      optionAdditionalText?: string;
    }[]
    attachments?: string[];
  }[]
  others?: string;
  surveyResponseId?: number | null;
  surveyId?: number | null;
  preAgentSurveyId?: number | null;
  preAgentId?: number | null;
  userId?: number | null;
  surveyResponseTitle?: string | null;
}

interface CreateCommerceApplicationCommonProps {
  commerceType: CommerceType;
  userId: number;
  commerceItemId: number;
  totalDiscount?: number;
  donationAmount?: number;
  paymentMethod: PaymentMethodType;
  totalPointEarnAfterApply?: number;
  applicationStatus: 'Applied' | 'WaitingList';
  CommerceApplicationOptions: {
    commerceApplicationOptionId?: number;
    commercePurchaseOptionId: number;
    numberOfOption: number;
  }[];
  SurveyResponses: CommerceApplicationSurveyResponseProps[];
}

interface CreateProductApplicationProps extends CreateCommerceApplicationCommonProps {
  commerceType: '貨品',
}

interface CreateEventApplicationProps extends CreateCommerceApplicationCommonProps {
  commerceType: '活動',
  CommerceScheduleApplications: {
    commerceScheduleId: number
  }[]
}

interface CreateCourseApplicationProps extends CreateCommerceApplicationCommonProps {
  commerceType: '課程',
  CommerceAttendees: {
    commerceAttendeeId?: number | null
    userId?: number | null
    preAgentId?: number | null
  }[]
  CommerceScheduleApplications: {
    commerceScheduleId: number
  }[]
}

export interface CreateCommerceApplicationByAdminProps {
  CommerceApplication: CreateProductApplicationProps | CreateEventApplicationProps | CreateCourseApplicationProps;
}

/**
 * Admin Create Commerce Application, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#post-/v2/commerces/items/application/adminCreate)
 */

export const createCommerceApplicationByAdmin = async (
  params: CreateCommerceApplicationByAdminProps,
) => {
  const { data } = await Axios.post<CommerceApplicationResponse>(
    '/commerces/items/application/adminCreate',
    params,
  );
  return data;
};

export interface UpdateCommerceApplicationByAdminProps {
  CommerceApplicationOptions: {
    commerceApplicationOptionId?: number;
    commercePurchaseOptionId: number;
    numberOfOption: number;
  }[];
  SurveyResponses: CommerceApplicationSurveyResponseProps[];
  CommerceAttendees: {
    commerceAttendeeId?: number | null
    userId?: number | null
    preAgentId?: number | null
  }[]
  CommerceScheduleApplications: {
    commerceScheduleId: number
  }[]
}

/**
 * Update Application. Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/application/-commerceApplicationId-)
 */

export const updateCommerceApplicationByAdmin = async (
  commerceApplicationId: number,
  params: UpdateCommerceApplicationByAdminProps,
) => {
  const { data } = await Axios.put<CommerceApplicationResponse>(
    `/commerces/items/application/${commerceApplicationId}`,
    params,
  );
  return data;
};

/**
 * Get Application List Excel JobId by commerceItemId, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/-commerceItemId-/applicationExcel)
 */

export const getCommerceApplicationListExcelJobId = async (
  commerceItemId: number,
) => {
  const { data } = await Axios.get<{ jobId: number }>(
    `/commerces/items/${commerceItemId}/applicationExcel`,
  );
  return data;
};

/**
 * Export Application List Excel by JobId, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/applicationExcelJob/-jobId-)
 */

export const exportCommerceApplicationListExcel = async (
  jobId: number,
) => {
  const { data } = await Axios.get<ArrayBuffer>(
    `/commerces/items/applicationExcelJob/${jobId}`,
    { responseType: 'arraybuffer' },
  );
  return data;
};

/**
 * Get Attendee List Excel JobId by commerceItemId, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/-commerceItemId-/attendeeListExcel)
 */

export const getCommerceAttendeeListExcelJobId = async (
  commerceItemId: number,
) => {
  const { data } = await Axios.get<{ jobId: number }>(
    `/commerces/items/${commerceItemId}/attendeeListExcel`,
  );
  return data;
};

/**
 * Export Attendee List Excel by JobId, Admin Only (https://svgroup-app-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/attendeeListExcelJob/-jobId-)
 */

export const exportCommerceAttendeeListExcel = async (
  jobId: number,
) => {
  const { data } = await Axios.get<ArrayBuffer>(
    `/commerces/items/attendeeListExcelJob/${jobId}`,
    { responseType: 'arraybuffer' },
  );
  return data;
};

/**
 * Export Receipt PDF by commerceApplicationId, Login Only (https://svgroup-user-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/application/-commerceApplicationId-/receipt)
 */

export const exportReceiptPDF = async (
  commerceApplicationId: number,
) => {
  const { data } = await Axios.get<ExportReceiptPDFResponse>(
    `/commerces/items/application/${commerceApplicationId}/receipt`,
  );
  return data;
};

/**
 * Export Donation Receipt PDF by commerceApplicationId, Login Only (https://svgroup-user-v2-dev.appicidea.com/v2/commerces/doc/v2#get-/v2/commerces/items/application/-commerceApplicationId-/donationReceipt)
 */

export const exportDonationReceiptPDF = async (
  commerceApplicationId: number,
) => {
  const { data } = await Axios.get<ExportReceiptPDFResponse>(
    `/commerces/items/application/${commerceApplicationId}/donationReceipt`,
  );
  return data;
};
